import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

// Create the views imports with lazy loading
const HomePage = () => import('@/views/HomePage.vue')
const AboutPage = () => import('@/views/AboutPage.vue')
const MembersPage = () => import('@/views/MembersPage.vue')
const MemberDetailPage = () => import('@/views/MemberDetailPage.vue')
const ProjectsPage = () => import('@/views/ProjectsPage.vue')
const ProjectDetailPage = () => import('@/views/ProjectDetailPage.vue')
const ContactPage = () => import('@/views/ContactPage.vue')

const SearchResultsPage = () => import('@/views/SearchResultsPage.vue')
const StaticPage = () => import('@/views/StaticPage.vue')
const WorkflowPage = () => import('@/views/WorkflowPage.vue')

// Define routes
const routes = [
  {
    path: '/',
    name: 'home',
    component: HomePage,
    meta: {
      title: '360° Collective | Visioneering World-Class Experiences'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: AboutPage,
    meta: {
      title: 'About | 360° Collective'
    }
  },
  {
    path: '/members',
    name: 'members',
    component: MembersPage,
    meta: {
      title: 'Our Collective | 360° Collective'
    }
  },
  {
    path: '/members/:id',
    name: 'member-detail',
    component: MemberDetailPage,
    props: true,
    meta: {
      title: 'Member | 360° Collective'
    },
    // Redirect member detail pages to homepage until implemented
    beforeEnter: (to, from, next) => {
      next({ name: 'home' });
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: ProjectsPage,
    meta: {
      title: 'Projects | 360° Collective'
    }
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: ProjectDetailPage,
    props: true,
    meta: {
      title: 'Project | 360° Collective'
    }
  },
  {
    path: '/workflow',
    name: 'workflow',
    component: WorkflowPage,
    meta: {
      title: 'Workstream Structure | 360° Collective'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactPage,
    meta: {
      title: 'Contact Us | 360° Collective'
    }
  },

  {
    path: '/search',
    name: 'search-results',
    component: SearchResultsPage,
    props: route => ({ query: route.query.q }),
    meta: {
      title: 'Search Results | 360° Collective'
    }
  },
  {
    path: '/:slug',
    name: 'static-page',
    component: StaticPage,
    props: true,
    meta: {
      title: 'Page | 360° Collective'
    },
    // Exclude existing routes with their own components and handle non-existent/unpublished pages
    beforeEnter: async (to, from, next) => {
      const reservedSlugs = ['about', 'members', 'projects', 'contact', 'search', 'workflow'];
      if (reservedSlugs.includes(to.params.slug)) {
        next({ name: to.params.slug });
        return;
      }
      
      // Check if this looks like a member/company name and redirect to homepage
      // Common patterns: company names with spaces (encoded as %20), design/production related terms
      const slug = decodeURIComponent(to.params.slug).toLowerCase();
      const memberKeywords = ['design', 'production', 'creative', 'studio', 'group', 'company', 'collective', 'agency', 'media', 'management'];
      const isLikelyMemberPage = memberKeywords.some(keyword => slug.includes(keyword)) || 
                                slug.includes(' ') || // Names with spaces
                                slug.length > 20; // Long names typical of companies
      
      if (isLikelyMemberPage) {
        next({ name: 'home' });
        return;
      }
      
      // Check if the static page exists and is published
      try {
        const staticPageService = await import('@/services/staticPageService');
        await staticPageService.default.getStaticPage(to.params.slug);
        // If page exists and is published, proceed normally
        next();
      } catch (error) {
        // If page doesn't exist, is unpublished, or backend redirects, redirect to home
        const status = error.response ? error.response.status : 0;
        console.log(`Static page "${to.params.slug}" not found (status: ${status}), redirecting to homepage`);
        next({ name: 'home' });
      }
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    beforeEnter: (to, from, next) => {
      console.log(`404 - Redirecting unknown path "${to.path}" to homepage`);
      next({ name: 'home' });
    }
  }
]

// Create router instance
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0 }
    }
  }
})

// Global navigation guards
router.beforeEach((to, from, next) => {
  // Show loading indicator
  store.dispatch('setLoading', true)
  
  // Set document title
  document.title = to.meta.title || process.env.VUE_APP_TITLE
  
  // Clear any previous errors
  store.dispatch('clearError')
  
  next()
})

router.afterEach(() => {
  // Hide loading indicator after navigation is complete
  // Small delay to ensure component is loaded
  setTimeout(() => {
    store.dispatch('setLoading', false)
  }, 200)
})

export default router 